import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Save } from "lucide-react";

const EMPTY = {
  business_name: "Maharlika Seafood Mart & Chow",
  restaurant_name: "ChowMaharlika",
  mart_name: "Maharlika Seafood Mart",
  tagline: "Where Filipino Tradition Meets Fresh Quality.",
  header_logo: "",
  footer_logo: "",
  admin_logo: "",
  login_logo: "",
  register_logo: "",
  restaurant_logo: "",
  grocery_logo: "",
  customer_logo: "",
  rewards_logo: "",
  lechon_logo: "",
  daily_menu_logo: "",
  receipt_logo: "",
  favicon: "",
  social_share_image: "",
  landing_restaurant_label: "CHOWMAHARLIKA",
  landing_restaurant_headline_line1: "Where Filipino Tradition",
  landing_restaurant_headline_line2: "Meets Fresh Quality.",
  landing_restaurant_description: "Authentic Filipino flavors. Slow-cooked adobo, sizzling sisig, crispy pata and more — made with love, served with pride.",
  landing_restaurant_primary_cta: "View Menu",
  landing_restaurant_secondary_cta: "Reserve Lechon",
  landing_grocery_label: "MAHARLIKA MART",
  landing_grocery_headline: "Your Filipino grocery destination",
  landing_grocery_description: "Fresh seafood, daily catches, Filipino pantry staples — quality products at honest prices. Tuloy po kayo!",
  landing_grocery_primary_cta: "Shop Now",
  landing_grocery_secondary_cta: "View Deals",
};

function LogoCard({ title, help, value, onChange, testId }) {
  return (
    <div className="admin-card p-5">
      <h2 className="font-heading text-xl text-[#D4AF37] mb-2">{title}</h2>
      <p className="text-xs text-[#FAF9F6]/50 mb-4">{help}</p>
      <ImageUpload
        value={value}
        onChange={onChange}
        recommendedSize="500 × 500"
        aspect="aspect-square"
        testId={testId}
      />
    </div>
  );
}

export default function AdminBranding() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/branding");
      setForm({ ...EMPTY, ...data });
    } catch {
      toast.error("Failed to load branding settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/branding", form);
      toast.success("Branding saved");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  if (loading) return <div className="text-[#FAF9F6]/70 p-4">Loading branding settings...</div>;

  const set = (key, value) => setForm({ ...form, [key]: value });

  return (
    <div data-testid="admin-branding" className="w-full max-w-7xl mx-auto px-3 sm:px-4 pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-3xl text-[#FAF9F6]">Branding</h1>
          <p className="text-sm text-[#FAF9F6]/50">Manage logos, page badges, and landing page wording.</p>
        </div>
        <button onClick={save} className="btn-chow w-full sm:w-auto flex items-center justify-center gap-2" data-testid="branding-save">
          <Save className="w-4 h-4" /> Save Branding
        </button>
      </div>

      <h2 className="font-heading text-2xl text-[#FAF9F6] mb-3">Core Logos</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
        <LogoCard title="Header Logo" help="Main website navigation logo." value={form.header_logo} onChange={(url) => set("header_logo", url)} testId="header-logo" />
        <LogoCard title="Footer Logo" help="Footer logo. Falls back to header logo." value={form.footer_logo} onChange={(url) => set("footer_logo", url)} testId="footer-logo" />
        <LogoCard title="Admin Logo" help="Admin sidebar and admin dashboard." value={form.admin_logo} onChange={(url) => set("admin_logo", url)} testId="admin-logo" />
        <LogoCard title="Receipt Logo" help="Order receipt and sales confirmation." value={form.receipt_logo} onChange={(url) => set("receipt_logo", url)} testId="receipt-logo" />
      </div>

      <h2 className="font-heading text-2xl text-[#FAF9F6] mb-3">Public Page Logos</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
        <LogoCard title="Restaurant Logo" help="Restaurant hero, restaurant menu, daily menu, and Chow sections." value={form.restaurant_logo} onChange={(url) => set("restaurant_logo", url)} testId="restaurant-logo" />
        <LogoCard title="Grocery Logo" help="Grocery hero and mart page badges." value={form.grocery_logo} onChange={(url) => set("grocery_logo", url)} testId="grocery-logo" />
        <LogoCard title="Rewards Logo" help="Rewards page badge. Falls back to restaurant logo." value={form.rewards_logo} onChange={(url) => set("rewards_logo", url)} testId="rewards-logo" />
        <LogoCard title="Lechon Logo" help="Lechon page badge. Falls back to restaurant logo." value={form.lechon_logo} onChange={(url) => set("lechon_logo", url)} testId="lechon-logo" />
      </div>

      <h2 className="font-heading text-2xl text-[#FAF9F6] mb-3">Account Logos</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
        <LogoCard title="Login Logo" help="Login page icon." value={form.login_logo} onChange={(url) => set("login_logo", url)} testId="login-logo" />
        <LogoCard title="Register Logo" help="Registration page icon. Falls back to login logo." value={form.register_logo} onChange={(url) => set("register_logo", url)} testId="register-logo" />
        <LogoCard title="Customer Logo" help="Customer dashboard badge." value={form.customer_logo} onChange={(url) => set("customer_logo", url)} testId="customer-logo" />
        <LogoCard title="Daily Menu Logo" help="Daily menu page badge. Falls back to restaurant logo." value={form.daily_menu_logo} onChange={(url) => set("daily_menu_logo", url)} testId="daily-menu-logo" />
      </div>

      <div className="admin-card p-5 mb-8">
        <h2 className="font-heading text-xl text-[#D4AF37] mb-4">Basic Text</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Business Name" className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
          <input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Tagline" className="bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
        </div>
      </div>

      <div className="admin-card p-5">
        <h2 className="font-heading text-xl text-[#D4AF37] mb-4">Landing Page Wording</h2>
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="space-y-3">
            <h3 className="text-[#FAF9F6] font-semibold">Restaurant Panel</h3>
            <input value={form.landing_restaurant_label} onChange={(e) => set("landing_restaurant_label", e.target.value)} placeholder="Restaurant Label" className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
            <input value={form.landing_restaurant_headline_line1} onChange={(e) => set("landing_restaurant_headline_line1", e.target.value)} placeholder="Restaurant Headline Line 1" className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
            <input value={form.landing_restaurant_headline_line2} onChange={(e) => set("landing_restaurant_headline_line2", e.target.value)} placeholder="Restaurant Headline Line 2" className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
            <textarea value={form.landing_restaurant_description} onChange={(e) => set("landing_restaurant_description", e.target.value)} placeholder="Restaurant Description" rows={4} className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
          </div>

          <div className="space-y-3">
            <h3 className="text-[#FAF9F6] font-semibold">Grocery Panel</h3>
            <input value={form.landing_grocery_label} onChange={(e) => set("landing_grocery_label", e.target.value)} placeholder="Grocery Label" className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
            <input value={form.landing_grocery_headline} onChange={(e) => set("landing_grocery_headline", e.target.value)} placeholder="Grocery Headline" className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
            <textarea value={form.landing_grocery_description} onChange={(e) => set("landing_grocery_description", e.target.value)} placeholder="Grocery Description" rows={4} className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}
